import { paths } from '@/paths'
import { getSafeDocumentsReturnUrl } from '@/utils/auth-return-url'
import { decodeWithoutBearer } from '@/utils/jwt'

/** « Terminer », échéance, ou session refusée par la gateway (fermée ailleurs, remplacée). */
export type ImpersonationEnd = 'ended' | 'expired' | 'closed'

/** Code à usage unique de la gateway (32 octets en base64url). */
const CODE_PATTERN = /^[A-Za-z0-9_-]{43}$/

/**
 * Un jeton « en tant que » porte `impersonator`, et son `exp` (en secondes) est la fin de la session :
 * une heure au plus (ADR 0044 du backend). C'est lui qui dit l'impersonation, pas `staffAccessToken` :
 * arrivé depuis la console, on n'avait pas forcément de session dans la webapp.
 */
export const readImpersonation = (
  token: string | null
): { expiresAt: number | null } | null => {
  if (!token) return null
  try {
    const payload = decodeWithoutBearer(token)
    if (!payload.impersonator) return null
    const exp = Number(payload.exp)
    return { expiresAt: Number.isFinite(exp) && exp > 0 ? exp * 1000 : null }
  } catch {
    return null
  }
}

/** À l'ouverture : la session à retrouver sur « Terminer », jamais un jeton « en tant que » remplacé. */
export const ownSessionToKeep = (
  current: string | null,
  kept: string | null
): string | null => {
  if (!current) return null
  return readImpersonation(current) ? kept : current
}

/**
 * À la fermeture : la session gardée. Si un autre onglet a déjà changé celle du stockage, partagé entre onglets
 * (session propre reprise, nouvelle session ouverte depuis la console), on la suit au lieu de l'écraser.
 */
export const sessionAfterEnd = (
  ours: string | null,
  current: string | null,
  kept: string | null
): string | null => (current && current !== ours ? current : kept)

/**
 * Le lien de la console : `/impersonate#code=…&next=…`. Dans le fragment, le code ne part vers aucun serveur
 * (ni Netlify, ni les journaux). `next` n'ouvre qu'une page de dossier, l'accueil sinon.
 */
export const readImpersonationLink = (
  hash: string
): { code: string; next: string } | null => {
  const params = new URLSearchParams(hash.replace(/^#/, ''))
  const code = params.get('code')
  if (!code || !CODE_PATTERN.test(code)) return null
  return {
    code,
    next: getSafeDocumentsReturnUrl(params.get('next'), paths.index),
  }
}
